import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  OutlinedInput,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Alert,
  Typography,
} from "@mui/material";
import { ClassData } from "../../hooks/useClasses";
import { Teacher } from "../../hooks/useTeacher";
import { Student } from "../../hooks/useStudents";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { SelectChangeEvent } from "@mui/material/Select";
import { ClassOptions } from "../enum/ClassOptions";
import { useSessionClass } from "contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";

interface ClassFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: ClassData;
  onSubmit: (formData: ClassData) => void;
  isEdit: boolean;
  schoolId: number;
  teachers: Teacher[];
  students: Student[];
  existingClasses?: ClassData[]; // List of existing classes for validation
}

const ClassFormModal: React.FC<ClassFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolId,
  teachers,
  students,
  existingClasses = [],
}) => {
  // Initial state for reset
  const initialFormState: ClassData = {
    name: "",
    section: "",
    schoolId: schoolId,
  };

  const [formData, setFormData] = useState<ClassData>(initialFormState);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const { selectedSession } = useSessionClass();
  const { t } = useTranslation();
  const { showError } = useError();

  // Reset or prefill form on open
  React.useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({ ...initialData, schoolId });
      } else {
        setFormData({ ...initialFormState, schoolId });
      }
    }
    // eslint-disable-next-line
  }, [open, initialData, isEdit, schoolId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;


    if (name === "section") {
      // Block spaces + convert to uppercase
      const cleaned = value.replace(/\s+/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: cleaned.toUpperCase(),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that we have a session selected
    if (!selectedSession || !selectedSession.id) {
      showError(t("please_select_a_session"));
      return;
    }

    // When editing, check if the new section already exists for the same class name
    if (isEdit && initialData?.id) {
      const duplicateClass = existingClasses.find(
        (cls) =>
          cls.id !== initialData.id && // Exclude the current class being edited
          cls.name === formData.name && // Same class name
          cls.section === formData.section && // Same section
          cls.schoolId === schoolId // Same school
      );

      if (duplicateClass) {
        showError(`A class already exists with class : ${formData.name} and section : ${formData.section}`);
        return;
      }
    }

    onSubmit({ ...formData });
    if (!isEdit) {
      setFormData({ ...initialFormState, schoolId });
    }
  };

  const isFormValid =
    formData.name &&
    formData.name.trim() !== "" &&
    formData.section &&
    formData.section.trim() !== "" &&
    selectedSession &&
    selectedSession.id;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {isEdit ? t("edit_class") : t("add_new_class")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Class Name Field */}
            <FormControl
              fullWidth
              margin="dense"
              required
              variant="outlined"
              size="small"
            >
              <InputLabel
                sx={{
                  "& .MuiFormLabel-asterisk": { color: "red" },
                }}
              >
                {t("class_name")}
              </InputLabel>
              <Select
                name="name"
                value={formData.name}
                onChange={handleSelectChange}
                label={t("class_name")}
                size="small"
                required
                input={<OutlinedInput label={t("class_name")} size="small" />}
                displayEmpty
                sx={{
                  minHeight: "40px",
                  display: "flex",
                  alignItems: "center",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                }}
                renderValue={(selected) =>
                  !selected ? (
                    <span style={{ color: "#aaa" }}></span>
                  ) : (
                    <span style={{ color: "black" }}>{selected}</span>
                  )
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                {Object.values(ClassOptions).map((classLabel) => (
                  <MenuItem key={classLabel} value={classLabel}>
                    {classLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Section Field */}
            <TextField
              name="section"
              label={t("section")}
              value={formData.section}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === " ") e.preventDefault(); // disable space key
              }}
              fullWidth
              margin="dense"
              required
              size="small"
              variant="outlined"
              sx={{
                "& .MuiFormLabel-asterisk": { color: "red" },
                "& .MuiOutlinedInput-root": {
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3092e3",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3092e3",
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", pb: 2, mr: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid}
            sx={{
              minWidth: 100,
              backgroundColor: "#3092e3",
              "&:hover": {
                backgroundColor: "primary.main",
              },
              "&.Mui-disabled": {
                backgroundColor: "#b2ebff",
                opacity: 0.6,
              },
            }}
          >
            {isEdit ? t("update") : t("create")}
          </Button>
          <Button
            type="button"
            onClick={handleClose}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: "#f44336", // red
              color: "#f44336",
              ml: 2,
              "&:hover": {
                borderColor: "#b71c1c",
                color: "#b71c1c",
                backgroundColor: "rgba(244,67,54,0.08)",
              },
            }}
          >
            {t("cancel")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClassFormModal;
